// "Continuar con Google, Apple o Facebook" en la app del chofer (decisiones D50 y D51). La app no habla con el
// proveedor: manda el navegador a la API (`/v1/auth/<p>/iniciar?app=chofer`), que va al proveedor y vuelve a
// `/acceso#c=<código de un uso>&p=<p>`. Para que ese código no sirva en otro celular, antes de ir se guarda acá un
// secreto al azar y viaja solo su SHA-256; al volver se canjea con el secreto por la sesión de siempre
// (`entrarConProveedor` en sesion.ts, que cuida lo que hay en el celular). Vincular desde "Mi cuenta" (Perfil) usa la
// misma ida y vuelta, con sesión, y vuelve a `/perfil#c=…&p=…&t=vincular`.
import {
  type ErrorGooglePersonal,
  ERRORES_GOOGLE_PERSONAL,
  leerVueltaGooglePersonal,
  PROVEEDORES,
  type Proveedor,
  type VueltaGooglePersonal,
} from '@reparto/shared/nucleo';
import { API_URL } from '../config';
import { api, ErrorApi, SinRed } from './api';

const CLAVE = 'reparto.chofer.google';
/** El ingreso dura 10 minutos del lado de la API: un secreto más viejo ya no sirve. */
const VIGENCIA_MS = 15 * 60_000;

interface SecretoGuardado {
  secreto: string;
  creado: number;
}

/** Lo que dice `GET /auth/opciones` y la app usa. */
export interface OpcionesAcceso {
  google: { chofer: boolean };
  apple: { chofer: boolean };
  facebook: { chofer: boolean };
  /** "Olvidé mi contraseña": `correo` (el servidor manda el enlace) o `pedir` (se lo pide al administrador). */
  recuperar: 'correo' | 'pedir';
  /** Demostración: el enlace para elegir la contraseña vuelve en la respuesta y se muestra en pantalla. */
  enlace_en_pantalla: boolean;
  /** Alguna empresa prendió "Entrar con el celular y un código". */
  celular: boolean;
}

/** Cómo se puede entrar. Null sin señal o sin respuesta: solo se ve el correo y la contraseña. */
export async function opcionesAcceso(): Promise<OpcionesAcceso | null> {
  try {
    const r = await api<Partial<OpcionesAcceso>>('/auth/opciones', { sinToken: true, timeoutMs: 8000 });
    const o: OpcionesAcceso = {
      google: { chofer: r.google?.chofer === true },
      apple: { chofer: r.apple?.chofer === true },
      facebook: { chofer: r.facebook?.chofer === true },
      recuperar: r.recuperar === 'correo' ? 'correo' : 'pedir',
      enlace_en_pantalla: r.enlace_en_pantalla === true,
      celular: r.celular === true,
    };
    try {
      localStorage.setItem(CLAVE_OPCIONES, JSON.stringify(o));
    } catch {
      // sin almacenamiento, la próxima vez no se reserva el lugar
    }
    return o;
  } catch {
    return null;
  }
}

const CLAVE_OPCIONES = 'reparto.chofer.opciones-acceso';

/**
 * Lo que dijo el servidor la última vez en este celular. Mientras llegan las opciones de ahora, las pantallas de acceso
 * reservan en gris lo que hubo (los botones de los proveedores; en "olvidé", el formulario o el aviso de pedírselo al
 * administrador): así, cuando llegan, nada salta. Sin el dato, null.
 */
export function opcionesRecordadas(): OpcionesAcceso | null {
  try {
    const o = JSON.parse(localStorage.getItem(CLAVE_OPCIONES) ?? 'null') as OpcionesAcceso | null;
    return o && typeof o === 'object' && typeof o.recuperar === 'string' ? o : null;
  } catch {
    return null;
  }
}

/** Cuántos botones de proveedor tuvo el acceso la última vez en este celular. */
export const proveedoresRecordados = (): number => proveedoresDe(opcionesRecordadas()).length;

/** Los proveedores que el servidor tiene para la app, en el orden de siempre (Google, Apple, Facebook). */
export const proveedoresDe = (o: OpcionesAcceso | null): Proveedor[] =>
  o ? PROVEEDORES.filter((p) => o[p].chofer) : [];

const aBase64url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

export function secretoAlAzar(): string {
  const b = new Uint8Array(32);
  crypto.getRandomValues(b);
  return aBase64url(b);
}

/** SHA-256 del secreto en base64url (lo que la API guarda y compara al canjear). */
export async function desafioDe(secreto: string): Promise<string> {
  const h = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(secreto));
  return aBase64url(new Uint8Array(h));
}

/** Dirección de la API que manda al proveedor. La vuelta la decide la API con su configuración, no la app. */
export function urlIniciar(proveedor: Proveedor, desafio: string): string {
  return `${API_URL}/v1/auth/${proveedor}/iniciar?${new URLSearchParams({ app: 'chofer', desafio }).toString()}`;
}

/** Prepara la ida: guarda un secreto nuevo y devuelve su desafío. */
export async function nuevoDesafio(): Promise<string> {
  const secreto = secretoAlAzar();
  try {
    localStorage.setItem(CLAVE, JSON.stringify({ secreto, creado: Date.now() } satisfies SecretoGuardado));
  } catch {
    // sin almacenamiento la vuelta dice que venció y se entra con la contraseña
  }
  return desafioDe(secreto);
}

/** Va al proveedor para entrar (deja la app: vuelve sola a `/acceso`). */
export async function irAProveedor(proveedor: Proveedor): Promise<void> {
  window.location.assign(urlIniciar(proveedor, await nuevoDesafio()));
}

/** "Mi cuenta": va al proveedor para vincularlo a la cuenta de la sesión (vuelve a `/perfil`). */
export async function irAVincular(proveedor: Proveedor): Promise<void> {
  const { url } = await api<{ url: string }>(`/auth/${proveedor}/vincular/iniciar`, {
    cuerpo: { app: 'chofer', desafio: await nuevoDesafio() },
  });
  window.location.assign(url);
}

/** El secreto de la ida (se usa una sola vez). Null si no hay, venció o se abrió en otro navegador. */
export function tomarSecreto(ahora = Date.now()): string | null {
  try {
    const crudo = localStorage.getItem(CLAVE);
    localStorage.removeItem(CLAVE);
    const s = crudo ? (JSON.parse(crudo) as SecretoGuardado) : null;
    return s?.secreto && ahora - s.creado < VIGENCIA_MS ? s.secreto : null;
  } catch {
    return null;
  }
}

/** Lee lo que dejó la API después de "#" (`c` o `error`, `p` y `t=vincular`) y lo borra de la dirección. */
export function tomarVuelta(): VueltaGooglePersonal | null {
  if (typeof window === 'undefined') return null;
  const v = leerVueltaGooglePersonal(window.location.hash);
  if (v) history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}`);
  return v;
}

/** El motivo para explicar cuando el canje falla (null: sin señal, se explica aparte). */
export function causaDelError(e: unknown): ErrorGooglePersonal | null {
  if (e instanceof SinRed) return null;
  if (!(e instanceof ErrorApi)) return 'google';
  if (e.codigo === 'GOOGLE_VENCIDO') return 'vencido';
  const causa = (e.detalles as { causa?: string } | undefined)?.causa;
  return causa && (ERRORES_GOOGLE_PERSONAL as readonly string[]).includes(causa)
    ? (causa as ErrorGooglePersonal)
    : 'google';
}

// ---------- "Mi cuenta" ----------

export interface CuentaPersonal {
  email: string | null;
  tiene_clave: boolean;
  proveedores: Array<{ proveedor: Proveedor; email: string | null; creado_en: string; ultimo_ingreso: string | null }>;
}

export const miCuenta = () => api<CuentaPersonal>('/auth/cuenta');

/** Termina de vincular con el código de la vuelta y el secreto de la ida. */
export const terminarVinculo = (codigo: string, secreto: string) =>
  api<{ vinculado: true; proveedor: Proveedor; email: string | null }>('/auth/vincular', {
    cuerpo: { codigo, secreto },
  });

export const desvincular = (proveedor: Proveedor) =>
  api<{ ok: true }>(`/auth/cuenta/proveedores/${proveedor}`, { metodo: 'DELETE' });
